import React from "react";

// ─── Types ──────────────────────────────────────────────────────────────────

export type ApplicationStatus = "OPERATIONAL" | "UNDER_DEVELOPMENT" | "DISPOSITION" | "OTHER";
export type ApplicationCriticality = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export type AssetRole = "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
export type AssetStatus = "RUNNING" | "STOPPED" | "STARTING" | "DEGRADED" | "MAINTENANCE" | "UNKNOWN";
export type AssetProtocol = "TCP" | "UDP" | "HTTP" | "HTTPS" | "GRPC";

// ─── Constants ───────────────────────────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  // Application Status
  OPERATIONAL: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  UNDER_DEVELOPMENT: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-400",
  DISPOSITION: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",

  // Asset Status
  RUNNING: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  STOPPED: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  STARTING: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  DEGRADED: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  MAINTENANCE: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  UNKNOWN: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",

  // Generic
  ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  INACTIVE: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
  OTHER: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
};

export const CRITICALITY_COLORS: Record<ApplicationCriticality, string> = {
  LOW: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  MEDIUM: "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
  HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  CRITICAL: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
};

export const ROLE_COLORS: Record<string, string> = {
  PRIMARY: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400",
  BACKUP: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
  STANDBY: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  READ_REPLICA: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  DR: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400",
  ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  PASSIVE: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
};

export const DEVICE_TYPE_COLORS: Record<string, string> = {
  SERVER: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  VM: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  FIREWALL: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  SWITCH: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  ROUTER: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  LB: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  CONTAINER: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  STORAGE: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
};

// ─── Components ──────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: string | null | undefined;
  type?: "status" | "criticality" | "role";
  className?: string;
  /**
   * When true, render a fixed-width centered pill matching the shared
   * StatusBadge used on Catalog/Profile pages, so badges line up at the same
   * size regardless of label length. Defaults to the content-sized chip.
   */
  pill?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, type = "status", className = "", pill = false }) => {
  if (!status) return null;
  const s = status.toUpperCase();

  let colorClass = STATUS_COLORS[s] || "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400";

  if (type === "criticality") {
    colorClass = CRITICALITY_COLORS[s as ApplicationCriticality] || colorClass;
  } else if (type === "role") {
    colorClass = ROLE_COLORS[s] || colorClass;
  }

  // Format label: UNDER_DEVELOPMENT -> Under Development
  const label = status
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  const shape = pill
    ? "inline-flex items-center justify-center w-[110px] px-3 py-0.5 rounded-full text-[11px] font-bold"
    : "px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap";

  return (
    <span className={`${shape} ${colorClass} ${className}`}>
      {label}
    </span>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function autoFillPort(cloudIdentifier: string, currentPort: string): string {
  if (currentPort) return currentPort;
  const id = cloudIdentifier.toLowerCase();
  if (id === "rds" || id === "aurora") return "3306";
  if (id === "elasticache") return "6379";
  if (id === "redshift") return "5439";
  if (id === "dynamodb") return "443";
  return "";
}

export function parseOptionalPort(value: string | number | null | undefined): number | null | undefined {
  if (value === null || value === undefined || value === "") return null;
  const port = Number(value);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return undefined; // Indicates error
  }
  return port;
}

export function flattenOrgHierarchy(nodes: any[], allowedTypes?: string[]): any[] {
  const out: any[] = [];
  for (const n of nodes) {
    if (n.isActive && (!allowedTypes || allowedTypes.includes(n.orgType))) {
      out.push(n);
    }
    if (n.children?.length) {
      out.push(...flattenOrgHierarchy(n.children, allowedTypes));
    }
  }
  return out;
}

/**
 * Initial zoom for a topology canvas, driven by how much data is on screen.
 *
 * A fixed multiplier on the fitView zoom does not work: fitView already zooms
 * in hard when there are only a few nodes, so multiplying pushes tiny graphs
 * straight into the maxZoom ceiling while dense graphs stay readable. Instead
 * pick an absolute zoom per data size - small graphs open slightly zoomed in
 * (nodes stay legible without filling the screen), large graphs open further
 * out so more of the tree is visible before the user pans.
 *
 * The result is clamped to the canvas min/max so it is always a valid zoom.
 */
export function topologyInitialZoom(
  nodeCount: number,
  opts?: { minZoom?: number; maxZoom?: number },
): number {
  const minZoom = opts?.minZoom ?? 0.05;
  const maxZoom = opts?.maxZoom ?? 2.5;

  const target =
    nodeCount <= 3
      ? 1.5
      : nodeCount <= 8
        ? 1.25
        : nodeCount <= 20
          ? 1.05
          : nodeCount <= 40
            ? 0.85
            : nodeCount <= 80
              ? 0.7
              : 0.55;

  return Math.min(maxZoom, Math.max(minZoom, target));
}
